import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { I18nextProvider } from 'react-i18next';
import { QueryClientProvider } from '@tanstack/react-query';
import { ErrorBoundary } from 'react-error-boundary';
import { queryClient } from '@/hooks/QueryClient.tsx';
import AuthProvider from '@/contexts/Auth/AuthProvider';
import ConsentProvider from '@/contexts/Consent/ConsentProvider';
import GlobalFeatureFlagsProvider from '@/contexts/FeatureFlags/GlobalFeatureFlagsProvider';
import Logger from '@/helpers/Log';
import ErrorFallback from './components/ErrorFallback/ErrorFallback';
import App from './App.tsx';
import i18n from './i18n/i18n.ts';
// HAT-1109: מעביר את הודעות ברירת המחדל של זוד לעברית. חייב להיטען לפני הטפסים.
import './i18n/zodHebrew.ts';
import './index.scss';

import { PostHogProvider } from 'posthog-js/react';
import posthogClient from '@/helpers/posthogClient';

// HAT-1081: לא Application Insights ולא PostHog נטענים כאן - ConsentProvider
// מפעיל את שניהם רק אחרי שהמשתמש הסכים לכלי המדידה. ל-PostHogProvider מועבר
// ה-singleton הלא-מאותחל, כדי ש-usePostHog ימשיך לעבוד בכל הקומפוננטות.

const handleError = (error: Error) => {
  Logger.error('Unexpected error caught by ErrorBoundary', error);
};

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <ErrorBoundary
      FallbackComponent={ErrorFallback}
      onError={handleError}
    >
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          {/* Above the router on purpose - the header, the app-root alerts and the
              management area all read flags from outside the fund routes. */}
          <GlobalFeatureFlagsProvider>
            <I18nextProvider i18n={i18n}>
              <PostHogProvider client={posthogClient}>
                <ConsentProvider>
                  <App />
                </ConsentProvider>
              </PostHogProvider>
            </I18nextProvider>
          </GlobalFeatureFlagsProvider>
        </AuthProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  </StrictMode>
);
